<template>
  <div class="" v-if="isPropertyLoading || isLoading">
    <Loading />
  </div>

  <div v-else>
    <BethsLayout :layoutTitle="`Account - ${property?.name}`">
      <div class="flex flex-col justify-center min-h-600">
        <!-- component -->
        <div
          class="m-auto my-10 border-2 border-gray-300 shadow-md rounded-md w-12/12 xl:6/12 lg:w-7/12 md:w-9/12 sm:12/12"
        >
          <div class="px-8 py-8 rounded-xl">
            <h1
              name="login-title"
              class="mt-2 flex flex-wrap content-center text-center font-bold text-2xl"
            >
              <div class="font-normal">Edit Account at&nbsp;</div>
              <div>
                {{ property.name }}
              </div>
            </h1>

            <div v-if="user">
              <div class="flex flex-row mx-0 my-4 text-sm">
                <span class="text-black text-md">Username:</span>
                <span class="pl-2 font-mono font-bold text-md">
                  {{ user.loginEmail }}
                </span>
              </div>
              <div class="flex flex-row mt-4">
                <div class="flex-1 mt-5 text-sm">
                  <BaseInput
                    v-model="firstName"
                    label="First Name"
                    type="text"
                    autocomplete="given-name"
                    class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    :error="errors.firstName"
                    :disabled="!isEditing"
                  />
                </div>
                <div class="flex-1 mt-5 ml-2 text-sm">
                  <BaseInput
                    v-model="lastName"
                    label="Last Name"
                    autocomplete="family-name"
                    class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    type="text"
                    :error="errors.lastName"
                    :disabled="!isEditing"
                  />
                </div>
              </div>

              <div class="flex flex-row">
                <div class="flex-1 mt-5 text-sm">
                  <BaseInput
                    v-model="primaryPhone"
                    label="Primary Phone"
                    typeof="text"
                    autocomplete="tel"
                    class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    :error="errors.primaryPhone"
                    placeholder="(123) 456-7890"
                    :disabled="!isEditing"
                  />
                </div>
                <div class="flex-1 mt-5 ml-2 text-sm">
                  <BaseInput
                    v-model="mobilePhone"
                    label="Mobile Phone"
                    typeof="text"
                    autocomplete="tel"
                    class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    :error="errors.mobilePhone"
                    placeholder="(123) 456-7890"
                    :disabled="!isEditing"
                  />
                </div>
              </div>

              <div class="my-5 text-sm" v-if="isEditing">
                <div class="">
                  <label for="invite" class="block text-black">Invite Code</label>
                </div>
                <div class="mt-2 ml-4">
                  <div v-for="code in inviteCodes" :key="code.inviteCodeId">
                    {{ code.code }}
                  </div>
                </div>
                <div v-if="inviteCodeResultMessage" class="w-max mt-3">
                  <span class="text-lg bg-yellow-400 py-2 px-4 rounded">
                    {{ inviteCodeResultMessage }}
                  </span>
                </div>

                <div class="flex">
                  <input
                    id="invite"
                    v-model="unappliedInviteCode"
                    type="text"
                    class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    placeholder="Invite code from welcome email"
                  />
                  <button
                    class="h-16 p-2 mt-2 bg-white border border-gray-500 rounded-sm"
                    aria-label="reload_invite"
                    type="button"
                    @click="lookupInviteCode"
                  >
                    <span v-if="loadingInviteCode">Loading...</span>
                    <span v-else>Apply</span>
                  </button>
                </div>
              </div>

              <div class="mt-4" v-if="property.isMobilityCheckEnabled">
                <BaseCheckbox
                  v-model="mobilityImpaired"
                  label="I have a Mobility Impairment"
                  :error="errors.mobilityImpaired"
                  :disabled="!isEditing"
                ></BaseCheckbox>
              </div>

              <div v-if="property.propertyType != PropertyType.SingleTenant" class="">
                <div class="flex-1 my-5 text-sm">
                  <label for="tenant" class="block text-black">
                    {{ property.tenantAlias ? property.tenantAlias : "Tenant" }}
                  </label>

                  <div class="relative">
                    <select
                      id="tenant"
                      v-model="tenant"
                      class="w-full px-4 py-3 text-xl font-mono appearance-none bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      :disabled="!isEditing || user?.isTenantAdmin"
                    >
                      <option
                        v-for="propTenant in tenants"
                        :key="propTenant.tenantId"
                        :value="propTenant.tenantId"
                      >
                        {{ propTenant.name }}
                      </option>
                    </select>
                    <div
                      class="absolute flex items-center px-2 pointer-events-none right-3 top-3 text-grey-darker"
                    >
                      <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path
                          d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div v-if="user?.isTenantAdmin" class="mt-1 text-sm text-gray-600 italic">
                    Tenant admins cannot change their tenant assignment
                  </div>
                </div>
              </div>

              <div
                id="training-program-section"
                class="flex flex-wrap"
                v-if="property?.isTrainingEnabled"
              >
                <div class="flex-1 my-5 text-sm">
                  <BaseCheckboxGroup
                    v-model="trainingProgram"
                    name="trainingProgram"
                    label="Training Program"
                    :options="trainingPrograms"
                    :error="errors.trainingProgram"
                    vertical
                    :disabled="!isEditing"
                  />
                  <div v-if="!trainingProgram || trainingProgram?.length == 0" class="text-red-800">
                    You must select at least one Training Program
                  </div>
                </div>
              </div>
              <div class="flex-1 my-5 text-sm">
                <BaseSelect
                  v-if="property.floorList?.length > 0"
                  label="Primary Floor"
                  class="w-full px-4 py-3 text-xl font-mono appearance-none bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                  v-model="primaryFloor"
                  :options="floorList"
                  :disabled="!isEditing"
                />
                <BaseInput
                  v-else
                  class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                  label="Primary Floor"
                  v-model="primaryFloor"
                  :disabled="!isEditing"
                />
              </div>

              <div class="flex-1 my-5 text-sm">
                <label for="roles" class="block text-black">Building Role</label>
                <div class="relative mt-2">
                  <select
                    id="roles"
                    v-model="role"
                    class="w-full px-4 py-3 text-xl font-mono appearance-none bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    :disabled="!isEditing"
                  >
                    <option
                      v-for="roleObj in roles"
                      :key="roleObj.siteRoleId"
                      :value="roleObj.siteRoleId"
                    >
                      {{ roleObj.roleAlias }}
                    </option>
                  </select>
                  <div
                    v-if="isEditing"
                    class="absolute flex items-center px-2 pointer-events-none right-3 top-3 text-grey-darker"
                  >
                    <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path
                        d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div v-if="property.isAlertEnabled" class="mb-8 mt-8">
                <hr class="mb-4" />
                <p class="text-black text-lg mb-2">Emergency Contact Info</p>
                <p class="text-sm mb-6">
                  If you would like to receive notifications about emergencies affecting this
                  building, enter an email address and/or SMS-enabled cell phone number.
                </p>

              <!-- Notification Opt In/Out -->
              <div v-if="property?.isAlertEnabled && (property as any)?.isPushNotificationsEnabled" class="mb-6">
                Push Notifications are
                <span class="font-bold">
                  {{ pushEnabled ? 'enabled' : 'disabled' }} </span>

                <button
                  v-if="pushEnabled"
                  class="text-sm text-blue-500 bg-blue-300 hover:bg-blue-900 hover:text-white ml-4 px-3 py-1"
                  @click="openDisablePopup()"
                >
                  <span class="font-bold">Disable</span>
                </button>
                <button
                  v-else
                  class="text-sm text-blue-500 bg-blue-300  hover:bg-blue-900 hover:text-white ml-4 px-3 py-3 mt-2"
                  @click="openEnablePopup()"
                >
                  <span class="font-bold">Enable Push Notifications</span>
                </button>
              </div>

                <div class="flex flex-row">
                  <div class="text-sm flex-1">
                    <BaseInput
                      v-model="alertPhone"
                      label="Emergency Alert SMS"
                      placeholder="Cell# to text Alerts"
                      type="text"
                      class="w-full px-4 text-base font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      autocomplete="tel"
                      :error="errors.alertPhone"
                    />
                    <Loading v-if="isOptInLoading" />
                    <p
                      v-else-if="optInError != null && optInError.length > 0"
                      class="mt-2 text-red-700"
                    >
                      {{ optInError }}
                    </p>
                    <span v-else>
                      <p v-if="!alertSmsChanged && isOptedOut" class="mt-2 text-red-700">
                        This phone number has opted out of alert SMS. Please check to make sure your
                        number is entered correctly and click the opt back in button. Note: You can
                        only attempt to opt back in once every 30 days.
                      </p>
                      <BaseButton
                        v-if="!alertSmsChanged && isOptedOut"
                        class="mt-2 border border-gray-500"
                        @click="optIn"
                      >
                        Opt Back In
                      </BaseButton>
                    </span>
                  </div>
                  <div class="ml-2 text-sm flex-1">
                    <BaseInput
                      v-model="alertEmail"
                      label="Emergency Alert Email"
                      placeholder="Email to send alerts"
                      type="text"
                      class="w-full px-4 text-base font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      autocomplete="email"
                      :error="errors.alertEmail"
                    />
                  </div>
                </div>

                <div>
                  <p class="mt-6 mb-3 text-sm">Select floors that you want notifications for:</p>
                  <div class="flex">
                    <BaseCheckbox class="grow mt-1" v-model="selectAllFloors" label="All Floors" />
                    <button
                      v-if="primaryFloor?.length > 0"
                      class="p-1 mr-2 bg-white border border-gray-500 rounded-sm text-sm"
                      type="button"
                      @click="
                        () => {
                          if (
                            primaryFloor &&
                            !selectAllFloors &&
                            !selectedFloors.includes(primaryFloor)
                          )
                            selectedFloors.push(primaryFloor);
                        }
                      "
                    >
                      Select my floor
                    </button>
                    <button
                      class="p-1 bg-white border border-gray-500 rounded-sm text-sm"
                      type="button"
                      @click="
                        () => {
                          if (!selectAllFloors) selectedFloors = [];
                        }
                      "
                    >
                      Clear Selection
                    </button>
                  </div>

                  <Listbox v-model="selectedFloors" multiple :disabled="selectAllFloors">
                    <div class="relative mt-3">
                      <ListboxButton
                        class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                      >
                        <span
                          :class="[selectAllFloors ? 'text-gray-500' : 'text-gray-800', 'text-md']"
                        >
                          {{
                            selectAllFloors
                              ? "All floors selected"
                              : selectedFloors.length > 0
                              ? selectedFloors.join(", ")
                              : "-- select floors --"
                          }}
                        </span>
                        <span
                          class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                        >
                          <i-heroicons-solid-selector
                            :class="[
                              selectAllFloors ? 'text-gray-300' : 'text-gray-600',
                              'w-5 h-5 text-2xl',
                            ]"
                            aria-hidden="true"
                          />
                        </span>
                      </ListboxButton>
                      <transition
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                      >
                        <ListboxOptions
                          class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                        >
                          <ListboxOption
                            v-for="floor in floorList"
                            :key="floor"
                            :value="floor"
                            as="template"
                          >
                            <li
                              :class="[
                                selectedFloors.includes(floor)
                                  ? 'border-1 bg-sky-300 border-blue-500'
                                  : 'hover:bg-blue-200',
                                'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                              ]"
                            >
                              {{ floor }}
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </div>
                  </Listbox>
                  <div class="ml-4 mt-2">
                    <span class="text-xs mb-8 text-gray-600 italic">
                      For SMS alerts, message and data rates may apply. You can opt out at any time
                      by removing the number from your profile or replying STOP. Contact your
                      building management for support or send email to support@akpreparedness.com.
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="errorSubmitting"
              class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
              role="alert"
            >
              <span class="block sm:inline">
                {{ errorSubmitting }}
              </span>
            </div>
            <div
              v-if="submitSuccess"
              class="p-4 text-green-700 bg-green-100 rounded-lg dark:bg-green-200 dark:text-green-800"
              role="alert"
            >
              <span class="block sm:inline">Successfully updated account!</span>
            </div>
            <div v-if="isSaving" class="w-full text-center"><Loading /></div>
            <div class="flex flex-wrap justify-between flex-row-reverse mt-8">
              <button
                v-if="isEditing"
                class="w-40 flex py-3 px-3 text-center text-white duration-300 bg-btn hover:bg-blue-500 rounded"
                @click="updateAccount"
              >
                <span class="i-icon i-icon-door-handle pl-3">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    width="28px"
                    height="28px"
                    stroke-width="1.5"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                    />
                  </svg>
                </span>
                <span class="flex-grow text-xl pr-1">Update</span>
              </button>

              <button
                v-if="!isEditing"
                class="w-40 flex py-3 px-3 text-center text-white duration-300 bg-btn hover:bg-blue-500 rounded"
                @click="isEditing = true"
              >
                Edit Account
              </button>
              <!-- <button
                class="block w-full p-3 py-4 mt-5 text-center text-white duration-300 bg-gray-800 rounded-sm hover:bg-black"
                @click="logout"
              >
                Log out
              </button> -->
            </div>
          </div>
        </div>
      </div>
    </BethsLayout>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from "../store/userStore";
import { usePersonStore } from "../store/personStore";
import { useBldgStore } from "../store/bldgStore";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import {
  InviteCodeDTO,
  SiteRoleDTO,
  PropertyType,
  SelfSavePersonCommand,
} from "../store/generatedFetchClient";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";
import { useAlertStore } from "../store/alertStore";
import { validateUnrequiredPhoneNumber } from "../helpers";
import { usePushNotificationActions } from "../composables/usePushNotifications";

const { notificationsEnabled: pushEnabled, openEnablePopup, openDisablePopup } = usePushNotificationActions();

onMounted(loadData);

var isPropertyLoading = computed(() => bldgStore.isLoading);
let isLoading = ref(false);
var property = computed(() => bldgStore.property);
var user = computed(() => userStore.user);
const userStore = useUserStore();
const peopleStore = usePersonStore();
const bldgStore = useBldgStore();
const alertStore = useAlertStore();
//const router = useRouter();//
var floorList = computed(() => {
  let floors = bldgStore.property?.floorList;
  return floors;
});
var tenants = computed(() => {
  if (property.value && property.value.tenants) {
    let tenants = property.value.tenants;
    let unrestrictedTenants = tenants.filter(
      (t) => !t.isRestricted || activatedTenantIds.value.find((a) => a == t.tenantId)
    );

    // For logged in user, add their current selected tenant
    if (user?.value.tenantId) {
      let userTenant = tenants.find((t) => t.tenantId === user?.value.tenantId);
      if (userTenant) {
        unrestrictedTenants.push(userTenant);
      }
    }

    //console.log("Active Tenants", unrestrictedTenants);
    return unrestrictedTenants;
  }
  return [];
});

let isSaving = ref(false);
let isEditing = ref(true);

var enableStaffRoles = ref(false);
var roles = computed(() => {
  if (bldgStore.property !== null) {
    let allRoles = bldgStore.property?.siteRoles ?? ([] as SiteRoleDTO[]);

    if (enableStaffRoles.value || user.value.isStaffRole) {
      return allRoles;
    }

    return allRoles.filter((r) => !r.isStaffRole || r.siteRoleId == user.value.siteRoleId);
  }
  return [];
});

var trainingPrograms = computed(function () {
  let bldg = bldgStore.building;
  if (bldg !== null) {
    let tp = bldg.trainingPrograms.filter((t) => {
      for (let inviteCode of inviteCodes.value) {
        if (inviteCode.trainingProgramIds.includes(t.trainingProgramId)) {
          return true;
        }
      }

      for (let permission of user.value.permissions) {
        if (permission.trainingProgramId == t.trainingProgramId) {
          return true;
        }
      }
      return false;
    });

    if (tp !== null && tp !== undefined) {
      let options = tp.map((t) => {
        return {
          name: t.name,
          value: t.trainingProgramId,
          isChecked: true,
        };
      });
      return options;
    }
  }
  return [];
});

let validationObj = {
  firstName: yup.string().required().label("First Name"),
  lastName: yup.string().required().label("Last Name"),

  primaryPhone: yup
    .string()
    .nullable()
    .notRequired()
    .max(50)
    .label("Primary Phone")
    .test(
      "valid-number",
      (d) => "Phone number is not valid",
      (value) => validateUnrequiredPhoneNumber(value)
    ),
  mobilePhone: yup
    .string()
    .nullable()
    .notRequired()
    .max(50)
    .label("Mobile Phone")
    .test(
      "valid-number",
      (d) => "Phone number is not valid",
      (value) => validateUnrequiredPhoneNumber(value)
    ),

  mobilityImpaired: yup.boolean().default(false),
  role: yup.number().required(),

  tenant: yup.number().nullable().notRequired(),
  primaryFloor: yup.string().nullable().notRequired().max(50).label("Primary Floor"),

  alertPhone: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      "valid-number",
      (d) => "Phone number is not valid",
      (value) => validateUnrequiredPhoneNumber(value)
    ),
  alertEmail: yup.string().nullable().notRequired(),
};

const validationSchema = yup.object(validationObj);

const { handleSubmit, errors } = useForm({
  validationSchema,
  initialValues: {
    firstName: "",
    lastName: "",

    primaryPhone: undefined,
    mobilePhone: undefined,

    mobilityImpaired: false,
    trainingProgram: [], //<--- Must initialize to empty array
    role: undefined,

    tenant: undefined,
    primaryFloor: "",

    alertPhone: undefined,
    alertEmail: undefined,
  },
});

var { value: firstName } = useField<string>("firstName");
var { value: lastName } = useField<string>("lastName");

var { value: primaryPhone } = useField<string>("primaryPhone");
var { value: mobilePhone } = useField<string>("mobilePhone");

var { value: mobilityImpaired } = useField<boolean>("mobilityImpaired");
var { value: trainingProgram } = useField<number[]>("trainingProgram");
var { value: role } = useField<number>("role");

var { value: tenant } = useField<number>("tenant");
var { value: primaryFloor } = useField<string>("primaryFloor");

const { value: alertPhone } = useField<string>("alertPhone");
const { value: alertEmail } = useField<string>("alertEmail");

const selectAllFloors = ref(false);
const selectedFloors = ref([] as string[]);

watch(selectedFloors, () => {
  selectedFloors.value.sort((a, b) => floorList.value.indexOf(a) - floorList.value.indexOf(b));
});

/**
 * InviteCode code
 */

let unappliedInviteCode = ref("");
let loadingInviteCode = ref(false);
let inviteCodeResultMessage = ref("");
let inviteCodes = ref<InviteCodeDTO[]>([]);
let activatedTenantIds = ref<number[]>([]);

async function lookupInviteCode() {
  if (property && property.value) {
    loadingInviteCode.value = true;
    inviteCodeResultMessage.value = "";

    if (!unappliedInviteCode.value) {
      inviteCodeResultMessage.value = "Please enter an Invite Code to lookup";
      loadingInviteCode.value = false;
      return;
    }

    let portalId = property.value.portalId ?? 0;

    let result = await bldgStore.lookupInviteCode(portalId, unappliedInviteCode.value);
    if (typeof result === "object") {
      let newCode = result as InviteCodeDTO;
      console.log("Found lookup code", newCode);
      if (!inviteCodes.value.find((i) => i.inviteCodeId === newCode.inviteCodeId)) {
        inviteCodes.value.push(newCode);
        activateCodes(inviteCodes.value);
      }
      unappliedInviteCode.value = "";
      inviteCodeResultMessage.value = "";
    } else {
      inviteCodeResultMessage.value = (result as string) ?? "";
    }
    loadingInviteCode.value = false;
  }
}

watch(property, loadData);
watch(user, populateForm);

//Populate form values
function populateForm() {
  if (user && user.value) {
    firstName.value = user.value.firstName;
    lastName.value = user.value.lastName;
    primaryPhone.value = user.value.primaryPhone;
    mobilePhone.value = user.value.mobilePhone;
    role.value = user.value.siteRoleId;
    trainingProgram.value = user.value.permissions
      .filter((p) => p.trainingProgramId != null && p.trainingProgramId > 0)
      .map((p) => p.trainingProgramId);
    tenant.value = user.value.tenantId;
    primaryFloor.value = user.value?.primaryFloor ?? "";
    mobilityImpaired.value = user.value.isMobilityImpaired ?? false;
    alertPhone.value = user.value.contactSms;
    alertEmail.value = user.value.contactEmail;
    selectAllFloors.value =
      user.value.alertSubscribedFloors == floorList.value.toString() ||
      user.value.alertSubscribedFloors == null;
    selectedFloors.value =
      user.value.alertSubscribedFloors != null && user.value.alertSubscribedFloors.length > 0
        ? user.value.alertSubscribedFloors.split(",")
        : [];
  }
}

async function loadData() {
  isLoading.value = true;
  if (property?.value?.portalId) {
    await userStore.getUser(property.value.siteCode, property.value.portalId, true);
    populateForm();
  }
  isLoading.value = false;
}

// async function logout() {
//   await userStore.logoff();
//   console.log("User successfully logged out");
//   router.push("/" + bldgStore.siteCode);
// }

//Set form state based on all active invite codes
function activateCodes(codes: InviteCodeDTO[]) {
  console.log("Activating invite code", codes);
  enableStaffRoles.value = false;

  codes.forEach((code) => {
    if (code.enableStaffRoles) {
      enableStaffRoles.value = true;
    }
    if (code.activatedTenantId) {
      activatedTenantIds.value.push(code.activatedTenantId);
      tenant.value = code.activatedTenantId;
    }
  });
}

let errorSubmitting = ref("");
let submitSuccess = ref(false);

let onInvalidSubmit = function (ctx: any) {
  submitSuccess.value = false;
  errorSubmitting.value = Object.values(ctx.errors).join(", ");
  console.log("error trying to submit form", ctx);
};

let updateAccount = handleSubmit(async (values) => {
  isSaving.value = true;
  submitSuccess.value = false;
  errorSubmitting.value = "";

  if (property?.value.isTrainingEnabled) {
    if (values.trainingProgram.length == 0) {
      isSaving.value = false;
      errorSubmitting.value = "You must have at least one active training program at this portal.";
      return;
    }
  }

  var savePerson = {
    portalId: property.value.portalId,
    personId: user.value.personId,
    firstName: values.firstName,
    lastName: values.lastName,
    primaryFloor: values.primaryFloor ?? "",
    mobilePhone: values.mobilePhone,
    primaryPhone: values.primaryPhone,
    contactEmail: values.alertEmail,
    contactSms: values.alertPhone,

    alertSubscribedFloors: selectAllFloors.value ? null : selectedFloors.value.toString(),

    isMobilityImpaired: values.mobilityImpaired,
    siteRoleId: values.role,
    trainingProgramIds: values.trainingProgram,
    tenantId: values.tenant,
  } as SelfSavePersonCommand;

  let error = await peopleStore.selfSavePerson(savePerson);
  if (!error) {
    // force get user so form is up to date
    await userStore.getUser(property.value.siteCode, property.value.portalId, true);

    isSaving.value = false;
    errorSubmitting.value = "";
    submitSuccess.value = true;
  } else {
    isSaving.value = false;
    errorSubmitting.value = error;
    submitSuccess.value = false;
  }
}, onInvalidSubmit);

//#region Opt in
var isOptedOut = computed(() => user.value.isSmsOptedOut ?? false);
var alertSmsChanged = computed(() => user.value.contactSms != alertPhone.value);
var isOptInLoading = ref(false);
var optInError = ref(null as string);

async function optIn() {
  isOptInLoading.value = true;
  var result = await alertStore.optIn(user.value.contactSms);
  await loadData();
  isOptInLoading.value = false;

  if (result != null && result.length > 0) optInError.value = result;
  else optInError.value = null;
}
//#endregion Opt in
</script>
